import { useEffect, useRef, useState } from "react";
import { Mic, Square, RotateCcw, Pause, Play } from "lucide-react";
import { Button } from "./ui/button";

export default function VoiceDictation({ onTranscript }) {
  const [supported, setSupported] = useState(true);
  const [listening, setListening] = useState(false);
  const [paused, setPaused] = useState(false);
  const recognitionRef = useRef(null);
  const baseTextRef = useRef("");

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setSupported(false);
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = "fr-FR";
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.onresult = (event) => {
      let finalText = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) finalText += event.results[i][0].transcript;
      }
      if (finalText) {
        baseTextRef.current = `${baseTextRef.current} ${finalText}`.trim();
        onTranscript(baseTextRef.current);
      }
    };
    recognition.onend = () => {
      if (listening && !paused) {
        try { recognition.start(); } catch {}
      }
    };
    recognitionRef.current = recognition;
    return () => {
      try { recognition.stop(); } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const start = (existingText) => {
    baseTextRef.current = existingText || "";
    setListening(true);
    setPaused(false);
    try { recognitionRef.current?.start(); } catch {}
  };

  const pause = () => {
    setPaused(true);
    setListening(false);
    try { recognitionRef.current?.stop(); } catch {}
  };

  const stop = () => {
    setListening(false);
    setPaused(false);
    try { recognitionRef.current?.stop(); } catch {}
  };

  const restart = () => {
    stop();
    baseTextRef.current = "";
    onTranscript("");
    setTimeout(() => start(""), 150);
  };

  if (!supported) {
    return (
      <div data-testid="voice-dictate-unsupported" className="text-xs text-muted-foreground italic border rounded-md p-2 bg-muted">
        La reconnaissance vocale n'est pas disponible sur ce navigateur. Saisissez le constat manuellement.
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {!listening ? (
        <Button
          type="button"
          data-testid="voice-dictate-button"
          onClick={() => start(baseTextRef.current)}
          className="bg-rose-600 hover:bg-rose-700 gap-2"
        >
          <Mic className="h-4 w-4" /> 🎙 Dicter le constat
        </Button>
      ) : (
        <Button type="button" data-testid="voice-dictate-pause-button" variant="secondary" onClick={pause} className="gap-2">
          <Pause className="h-4 w-4" /> Pause
        </Button>
      )}
      {paused && (
        <Button type="button" data-testid="voice-dictate-resume-button" variant="secondary" onClick={() => start(baseTextRef.current)} className="gap-2">
          <Play className="h-4 w-4" /> Reprendre
        </Button>
      )}
      {(listening || paused) && (
        <Button type="button" data-testid="voice-dictate-stop-button" variant="outline" onClick={stop} className="gap-2">
          <Square className="h-4 w-4" /> Arrêter
        </Button>
      )}
      <Button type="button" data-testid="voice-dictate-restart-button" variant="ghost" onClick={restart} className="gap-2">
        <RotateCcw className="h-4 w-4" /> Recommencer
      </Button>
      {listening && !paused && (
        <span className="text-xs text-rose-600 pulse-dot font-mono">● Écoute en cours...</span>
      )}
    </div>
  );
}
